import { AutocompleteInput, ReferenceInput } from "@/components/admin";
import { useCreate } from "ra-core";
import { useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";

interface FilteredContactInputProps {
    source?: string;
    label?: string | false;
    className?: string;
    /** Enable inline creation of a new contact for the selected company.
     *  Defaults to true (X-1 / QTN-1: the inline "+" add-contact must be
     *  available on every document form). Pass allowCreate={false} to opt out. */
    allowCreate?: boolean;
}

export const FilteredContactInput = ({ source = "contact_id", label = "Contact Person", className, allowCreate = true }: FilteredContactInputProps) => {
    const companyId = useWatch({ name: 'company_id' });
    const { setValue } = useFormContext();
    const [create] = useCreate();
    const isFirst = useRef(true);

    useEffect(() => {
        if (isFirst.current) {
            isFirst.current = false;
            return;
        }
        setValue(source, null);
    }, [companyId, source, setValue]);

    if (!companyId) {
        return (
            <div className="text-xs text-slate-400 bg-slate-50 border border-slate-200 rounded-md px-3 py-3 h-10 flex items-center">
                Select a Company first to pick a contact
            </div>
        );
    }
    return (
        <ReferenceInput key={companyId} source={source} reference="contacts" filter={{ company_id: companyId }}>
            <AutocompleteInput
                label={label}
                className={className}
                onCreate={allowCreate ? async (value?: string) => {
                    const full = value?.trim();
                    if (!full) return undefined;
                    const [first, ...rest] = full.split(" ");
                    const { data } = await create(
                        "contacts",
                        { data: { first_name: first, last_name: rest.join(" "), company_id: companyId } },
                        { returnPromise: true },
                    );
                    return data;
                } : undefined}
            />
        </ReferenceInput>
    );
};
